/* eslint-disable @typescript-eslint/ban-ts-comment */
import { autobind } from "core-decorators";
import BaseController from "@/components/mapbox/Lib/Controllers/BaseController";
import MapboxViewer from "@/components/mapbox/MapboxViewer";
import * as mapboxgl from "mapbox-gl";
import { Point } from "mapbox-gl";
import useDrawingStore from "@/stores/useDrawingStore";
import { DrawingTool, FeatureTypeEnum } from "@/types/enum";
import type { CreateFeatureDto } from "@/types/dto";
import { calculateArea } from "@/utils/geometry-utils";
class MeasureAreaController extends BaseController {
  public static name = "MeasureAreaController";
  public static event = {
    COMPLETE: "complete",
  };

  private _positions: [number, number][] = [];
  private _tempSourceId: string | null = null;
  private _tempLayerId: string | null = null;
  private _tempOutlineLayerId: string | null = null;
  private _tempSegmentLabelsSourceId: string | null = null;
  private _tempSegmentLabelsLayerId: string | null = null;
  private _lastPosition: [number, number] | null = null;

  constructor(mapboxViewer: MapboxViewer) {
    super(mapboxViewer);
    this._mapboxViewer = mapboxViewer;
  }

  public set enabled(value: boolean) {
    this._enabled = value;
    const canvas = this._mapboxViewer._mapViewer.getCanvas();
    if (value) {
      canvas.style.cursor = "crosshair";
      canvas.addEventListener("pointerdown", this.onPointerDownController);
      canvas.addEventListener("pointermove", this.onPointerMoveController);
      canvas.addEventListener("dblclick", this.handleDoubleClick);
      window.document.addEventListener("keydown", this.handleKeyDown);
    } else {
      this.dispose();
    }
  }

  public get enabled() {
    return this._enabled;
  }

  @autobind
  protected override handleClick(event: PointerEvent): boolean | void {
    event.preventDefault();

    if (event.button !== 0 && event.which !== 1) {
      return true;
    }

    if (
      this._onDownPosition.x === this._onUpPosition.x &&
      this._onDownPosition.y === this._onUpPosition.y
    ) {
      const canvas = this._mapboxViewer._mapViewer.getCanvas();
      const rect = canvas.getBoundingClientRect();
      const point = new Point(
        event.clientX - rect.left,
        event.clientY - rect.top
      );
      const lngLat = this._mapboxViewer._mapViewer.unproject(point);
      const pickedPosition: [number, number] = [lngLat.lng, lngLat.lat];

      // Check if clicking on the first point to close the polygon
      if (
        this._positions.length > 2 &&
        this._arePositionsEqual(this._positions[0], pickedPosition)
      ) {
        this.complete();
        return;
      }

      // Check if the same point is clicked twice in a row to complete
      if (
        this._lastPosition &&
        this._arePositionsEqual(this._lastPosition, pickedPosition) &&
        this._positions.length > 2
      ) {
        this.complete();
        return;
      }

      this._positions.push(pickedPosition);
      this._lastPosition = [...pickedPosition] as [number, number];
    }
  }

  @autobind
  handleDoubleClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (this._positions.length > 2) {
      this.complete();
    }
  }

  @autobind
  protected override onPointerMoveController(event: PointerEvent) {
    const canvas = this._mapboxViewer._mapViewer.getCanvas();
    const rect = canvas.getBoundingClientRect();
    const point = new Point(
      event.clientX - rect.left,
      event.clientY - rect.top
    );
    const lngLat = this._mapboxViewer._mapViewer.unproject(point);
    const currentCoord: [number, number] = [lngLat.lng, lngLat.lat];

    if (this._positions.length < 1) {
      return;
    }

    let polygonCoords: [number, number][];

    if (this._positions.length === 1) {
      polygonCoords = [...this._positions, currentCoord];
    } else if (this._positions.length === 2) {
      polygonCoords = [...this._positions, currentCoord, this._positions[0]];
    } else {
      polygonCoords = [...this._positions, currentCoord, this._positions[0]];
    }
    canvas.style.cursor = "crosshair";
    if (!this._tempSourceId) {
      this._tempSourceId = "temp-measure-area-source";
      this._tempLayerId = "temp-measure-area-layer";
      this._tempOutlineLayerId = "temp-measure-area-outline-layer";

      this._mapboxViewer._mapViewer.addSource(this._tempSourceId, {
        type: "geojson",
        data: {
          type: "Feature",
          geometry:
            this._positions.length < 2
              ? {
                  type: "LineString",
                  coordinates: polygonCoords,
                }
              : {
                  type: "Polygon",
                  coordinates: [polygonCoords],
                },
          properties: {},
        },
      });

      if (this._positions.length < 2) {
        this._mapboxViewer._mapViewer.addLayer({
          id: this._tempLayerId,
          type: "line",
          source: this._tempSourceId,
          layout: {},
          paint: {
            "line-color": "#4ecdc4",
            "line-width": 2,
            "line-dasharray": [2, 1],
          },
        });
      } else {
        this._mapboxViewer._mapViewer.addLayer({
          id: this._tempLayerId,
          type: "fill",
          source: this._tempSourceId,
          layout: {},
          paint: {
            "fill-color": "#4ecdc4",
            "fill-opacity": 0.2,
          },
        });

        this._mapboxViewer._mapViewer.addLayer({
          id: this._tempOutlineLayerId,
          type: "line",
          source: this._tempSourceId,
          layout: {},
          paint: {
            "line-color": "#4ecdc4",
            "line-width": 2,
            "line-dasharray": [2, 1],
          },
        });
      }
    } else {
      const source = this._mapboxViewer._mapViewer.getSource(
        this._tempSourceId
      ) as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData({
          type: "Feature",
          geometry:
            this._positions.length < 2
              ? {
                  type: "LineString",
                  coordinates: polygonCoords,
                }
              : {
                  type: "Polygon",
                  coordinates: [polygonCoords],
                },
          properties: {},
        });
      }
    }

    // Update or create segment length labels
    const segmentLabels = this.getSegmentLabels(polygonCoords);

    if (!this._tempSegmentLabelsSourceId) {
      this._tempSegmentLabelsSourceId = "temp-measure-area-segments-source";
      this._tempSegmentLabelsLayerId = "temp-measure-area-segments-layer";

      this._mapboxViewer._mapViewer.addSource(this._tempSegmentLabelsSourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: segmentLabels.map((label) => ({
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: label.coordinates,
            },
            properties: {
              length: label.length,
            },
          })),
        },
      });

      this._mapboxViewer._mapViewer.addLayer({
        id: this._tempSegmentLabelsLayerId,
        type: "symbol",
        source: this._tempSegmentLabelsSourceId,
        layout: {
          "text-field": ["get", "length"],
          "text-size": 12,
          "text-anchor": "center",
          "text-offset": [0, -0.5],
        },
        paint: {
          "text-color": "#4ecdc4",
          "text-halo-color": "#ffffff",
          "text-halo-width": 2,
        },
      });
    } else {
      const segmentLabelsSource = this._mapboxViewer._mapViewer.getSource(
        this._tempSegmentLabelsSourceId
      ) as mapboxgl.GeoJSONSource;
      if (segmentLabelsSource) {
        segmentLabelsSource.setData({
          type: "FeatureCollection",
          features: segmentLabels.map((label) => ({
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: label.coordinates,
            },
            properties: {
              length: label.length,
            },
          })),
        });
      }
    }
  }

  @autobind
  handleKeyDown(event: KeyboardEvent) {
    if (!this._enabled) {
      return;
    }
    if (event.key === "Escape") {
      this.reset();
    } else if (event.key === "Enter") {
      if (this._positions.length > 2) {
        this.complete();
      }
    }
  }

  @autobind
  complete() {
    if (!this._enabled) {
      return;
    }
    if (this._positions.length > 2) {
      // Close the polygon by adding the first point as the last point
      const closedCoordinates = [...this._positions, this._positions[0]];
      const area = calculateArea(closedCoordinates);

      const content: CreateFeatureDto = {
        name: "Measure Area",
        type: FeatureTypeEnum.MEASUREMENT_AREA,
        coordinates: closedCoordinates,
        properties: {
          area: area,
          unit: "m²",
          measureType: "area",
        },
        config: {
          visible: true,
          color: "#4ecdc4",
          isChecked: true,
        },
        projectId: "",
      };
      //@ts-ignore
      this.dispatchEvent({
        type: MeasureAreaController.event.COMPLETE,
        content: content,
      });

      // Reset for next measurement
      this.reset();
      useDrawingStore.getState().setActiveTool(DrawingTool.NONE);
    }
  }

  @autobind
  reset() {
    if (!this.enabled) {
      return;
    }
    this._removeTempLayer();
    this._positions = [];
    this._tempSourceId = null;
    this._tempLayerId = null;
    this._tempOutlineLayerId = null;
    this._lastPosition = null;
  }

  @autobind
  dispose() {
    super.dispose();
    const canvas = this._mapboxViewer._mapViewer.getCanvas();
    if (canvas) {
      canvas.style.cursor = "default";
      canvas.removeEventListener("pointerdown", this.onPointerDownController);
      canvas.removeEventListener("pointermove", this.onPointerMoveController);
      canvas.removeEventListener("dblclick", this.handleDoubleClick);
      window.document.removeEventListener("keydown", this.handleKeyDown);
    }
    this._removeTempLayer();
    this._positions = [];
    this._tempSourceId = null;
    this._tempLayerId = null;
    this._tempOutlineLayerId = null;
    this._lastPosition = null;
  }

  private _removeTempLayer() {
    if (
      this._tempLayerId &&
      this._mapboxViewer._mapViewer.getLayer(this._tempLayerId)
    ) {
      this._mapboxViewer._mapViewer.removeLayer(this._tempLayerId);
    }
    if (
      this._tempOutlineLayerId &&
      this._mapboxViewer._mapViewer.getLayer(this._tempOutlineLayerId)
    ) {
      this._mapboxViewer._mapViewer.removeLayer(this._tempOutlineLayerId);
    }
    if (
      this._tempSourceId &&
      this._mapboxViewer._mapViewer.getSource(this._tempSourceId)
    ) {
      this._mapboxViewer._mapViewer.removeSource(this._tempSourceId);
    }

    // Remove segment labels layer and source
    if (
      this._tempSegmentLabelsLayerId &&
      this._mapboxViewer._mapViewer.getLayer(this._tempSegmentLabelsLayerId)
    ) {
      this._mapboxViewer._mapViewer.removeLayer(this._tempSegmentLabelsLayerId);
    }
    if (
      this._tempSegmentLabelsSourceId &&
      this._mapboxViewer._mapViewer.getSource(this._tempSegmentLabelsSourceId)
    ) {
      this._mapboxViewer._mapViewer.removeSource(
        this._tempSegmentLabelsSourceId
      );
    }
    this._tempSegmentLabelsSourceId = null;
    this._tempSegmentLabelsLayerId = null;
  }

  private getSegmentLabels(
    coords: [number, number][]
  ): { coordinates: [number, number]; length: string }[] {
    const labels: { coordinates: [number, number]; length: string }[] = [];

    for (let i = 0; i < coords.length - 1; i++) {
      const start = coords[i];
      const end = coords[i + 1];
      const length = this.calculateDistance(start, end);

      // Calculate midpoint for label placement
      const midpoint: [number, number] = [
        (start[0] + end[0]) / 2,
        (start[1] + end[1]) / 2,
      ];

      labels.push({
        coordinates: midpoint,
        length: this.formatLength(length),
      });
    }

    return labels;
  }

  private calculateDistance(
    point1: [number, number],
    point2: [number, number]
  ): number {
    const R = 6371000; // Earth's radius in meters
    const lat1 = (point1[1] * Math.PI) / 180;
    const lat2 = (point2[1] * Math.PI) / 180;
    const deltaLat = ((point2[1] - point1[1]) * Math.PI) / 180;
    const deltaLng = ((point2[0] - point1[0]) * Math.PI) / 180;

    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) *
        Math.cos(lat2) *
        Math.sin(deltaLng / 2) *
        Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  private formatLength(length: number): string {
    if (length >= 1000) {
      return `${(length / 1000).toFixed(2)} km`;
    } else {
      return `${length.toFixed(2)} m`;
    }
  }

  private _arePositionsEqual(
    pos1: [number, number],
    pos2: [number, number]
  ): boolean {
    const tolerance = 1e-6;
    return (
      Math.abs(pos1[0] - pos2[0]) < tolerance &&
      Math.abs(pos1[1] - pos2[1]) < tolerance
    );
  }
}

export default MeasureAreaController;
